import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Check, X, ShieldCheck, Flag, Undo2, AlertTriangle } from 'lucide-react';

// Verbatim per approved design — shown on the attestation modal AND persistently
// on the ambient checklist panel. Attestation with financial consequence is
// never silent (design H, amended).
export const FEE_WARNING = "⚠️ Review each item carefully. Marking an item Done that was not done carries a $20 fee per item. If something was not possible, or does not apply to this project, press \"Can't do this\" and give a brief reason. There is no fee for an honest answer.";

// The last item on every first draft's checklist (backend app/notes_confirmation.py, same id): "I read this
// client's notes again and my edit follows them." Always there, even with no must-haves. It can only be marked
// done ("Can't do this" stays with the must-haves), and the $20 fee applies to it as to a must-have (Ryan,
// 2026-09-25), so a checklist of this item alone carries its own warning.
export const NOTES_ITEM_ID = 'notes_read';
export const NOTES_FEE_WARNING = 'Mark this Done only if it is true. Marking it Done when it was not carries a $20 fee.';
const hasMustHaves = (items) => (items || []).some(i => i.id !== NOTES_ITEM_ID);

// ── Ambient checklist (editor works through it; carries the fee warning) ──
// `onConfirm`: where no upload button opens the attestation (photo, culling), the panel carries its own.
export function MustHavesPanel({ projectId, gate, onChanged, onConfirm }) {
  if (!gate?.required || !(gate.items || []).length) return null;
  const items = gate.items;
  const done = items.filter(i => i.checked).length;
  const must = hasMustHaves(items);
  // Category-aware header (2026-07-15). Server returns gate.category —
  // 'highlight' | 'doc_edit' | 'social' | null. Falls back to generic label
  // for legacy responses that don't carry the field (transition safety).
  const CAT_LABELS = { highlight: 'Highlight Videos', doc_edit: 'Doc Edits', social: 'Social Media Videos' };
  const catSuffix = CAT_LABELS[gate.category] ? ` — ${CAT_LABELS[gate.category]}` : '';
  const toggle = async (item) => {
    try {
      await api.put(`/projects/${projectId}/must-haves/${item.id}/check`, { checked: !item.checked });
      onChanged();
    } catch (e) { toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to update'); }
  };
  return (
    <div className="rounded-md border border-ug-gold/25 bg-ug-gold/[0.04] p-3" data-testid="must-haves-panel">
      <div className="flex items-center gap-2 mb-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-ug-gold shrink-0" />
        <span
          data-testid="mh-panel-header"
          className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold flex-1">
          {must ? `Client Must-Haves${catSuffix}` : 'Before you deliver'}
        </span>
        <span className="tap-auto font-mono text-[9px] text-ug-gold/70" data-testid="mh-panel-progress">{done}/{items.length}</span>
      </div>
      <p className="tap-auto text-[9px] text-amber-200/70 leading-relaxed mb-2" data-testid="mh-panel-fee-warning" data-tone="warning">{must ? FEE_WARNING : NOTES_FEE_WARNING}</p>
      <div>
        {items.map((m, i) => (
          <button key={m.id} type="button" onClick={() => toggle(m)} data-testid={`mh-panel-item-${i}`}
            className="tap-auto w-full flex items-start gap-2 p-1.5 rounded hover:bg-white/[0.04] transition-colors text-left group mb-2 last:mb-0">
            <span className={`w-3.5 h-3.5 rounded-[3px] border shrink-0 mt-[1px] flex items-center justify-center transition-colors ${m.checked ? 'bg-oxidized-teal/80 border-oxidized-teal' : 'border-ug-gold/40 group-hover:border-ug-gold/70'}`}>
              {m.checked && <Check className="w-2.5 h-2.5 text-obsidian" strokeWidth={3} />}
            </span>
            <span className={`text-[11px] leading-snug break-words ${m.checked ? 'text-zinc-500 line-through' : 'text-zinc-100/85'}`}>{m.text}</span>
          </button>
        ))}
      </div>
      <p className="text-[9px] text-slate-500 mt-1.5 italic">Checked items arrive pre-confirmed in the first-draft attestation.</p>
      {onConfirm && (
        <Button size="sm" onClick={onConfirm} data-testid="mh-panel-confirm"
          className="tap-auto mt-2 h-8 bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 font-sans text-[10px] font-semibold">
          Confirm checklist
        </Button>
      )}
    </div>
  );
}

// The first-draft checklist where photo and culling deliver (video keeps its own in VideoReview). `active`: an
// editor about to deliver. Fail-open on a fetch error, like video: the deliver routes refuse without it anyway.
// `pending` holds the deliver control shut while it loads; `needed` until the attestation carries the last item.
export function useFirstDraftChecklist(projectId, active) {
  const [gate, setGate] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(async () => {
    if (!active) return;
    try { const r = await api.get(`/projects/${projectId}/must-haves`); setGate(r.data); setFailed(false); } catch { setFailed(true); }
  }, [projectId, active]);
  useEffect(() => { load(); }, [load]);
  const confirmed = (attestation) => setGate(g => ({ ...(g || {}), attestation, confirmed: true, pending_items: [] }));
  return {
    gate: gate && { ...gate, items: gate.pending_items || [] },   // the panel and the modal show what is still to confirm
    pending: !!(active && !gate && !failed),
    needed: !!(active && gate?.required && !gate?.confirmed),
    load, confirmed,
  };
}

// ── First-draft attestation modal (Part A gate) ──
// Per-item [Done] / [N/A + required reason]; NO select-all anywhere. Ambient
// pre-checks arrive pre-marked Done. Fully-pre-checked list = quick one-click
// confirm that still carries the fee warning (design H, amended).
export function AttestationModal({ projectId, items, onComplete, onClose }) {
  const [decisions, setDecisions] = useState(() => {
    const d = {};
    items.forEach(m => { if (m.checked) d[m.id] = { state: 'done', pre: true }; });
    return d;
  });
  const [submitting, setSubmitting] = useState(false);
  const must = hasMustHaves(items);
  const allPre = items.length > 0 && items.every(m => m.checked);
  const resolved = items.filter(m => {
    const d = decisions[m.id];
    return d && (d.state === 'done' || (d.state === 'na' && (d.na_reason || '').trim().length >= 5));
  }).length;
  const canSubmit = resolved === items.length;
  const setState = (id, state) => setDecisions(prev => ({ ...prev, [id]: { ...(prev[id] || {}), state } }));
  const setReason = (id, na_reason) => setDecisions(prev => ({ ...prev, [id]: { ...(prev[id] || {}), na_reason } }));
  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/projects/${projectId}/attestation`, {
        items: items.map(m => ({ id: m.id, state: decisions[m.id].state, na_reason: (decisions[m.id].na_reason || '').trim() })),
      });
      toast.success('Attestation recorded');
      onComplete(r.data.attestation);
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : (det?.message || 'Attestation failed — please try again'));
      setSubmitting(false);
    }
  };
  return (
    <div className="ug-modal fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="attestation-modal">
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl border border-ug-gold/30 bg-ug-deepest shadow-2xl">
        <div className="p-4 border-b border-ug-border sticky top-0 bg-ug-deepest z-10">
          <div className="flex items-start justify-between gap-3">
            <h3 data-role="section-title" className="font-sans text-[13px] font-semibold text-zinc-100">
              {must ? 'First-Draft Attestation — Client Must-Haves' : 'Before you deliver'}
            </h3>
            <button type="button" onClick={onClose} data-testid="attestation-close-btn"
              className="tap-auto text-slate-500 hover:text-zinc-200 transition-colors p-0.5"><X className="w-4 h-4" /></button>
          </div>
          <div className="mt-2 p-2.5 rounded-md bg-amber-500/[0.08] border border-amber-500/25" data-testid="attestation-fee-warning">
            <p className="text-[10px] text-amber-200/90 leading-relaxed">{must ? FEE_WARNING : NOTES_FEE_WARNING}</p>
          </div>
        </div>
        <div className="p-4">
          {/* NOTE: sibling-margin utilities (space-y-*) don't reach these mapped
              items in the dev preview — the visual-edits babel plugin wraps JSX
              expression children in an anonymous <span>, so the gap lives on the
              items themselves (mb-* last:mb-0), identical in dev and prod. */}
          {allPre ? (
            <>
              <div data-testid="attestation-prechecked-list">
                {items.map((m, i) => (
                  <div key={m.id} className="tap-auto flex items-start gap-2 p-2 rounded-md bg-oxidized-teal/[0.06] border border-oxidized-teal/20 mb-2 last:mb-0" data-testid={`attestation-pre-item-${i}`}>
                    <Check className="w-3.5 h-3.5 text-oxidized-teal shrink-0 mt-[1px]" strokeWidth={2.5} />
                    <span className="text-[11px] text-zinc-100/85 leading-snug break-words flex-1">{m.text}</span>
                    <span className="font-mono text-[8px] text-oxidized-teal/70 shrink-0">DONE</span>
                  </div>
                ))}
              </div>
              {must && (
                <p className="text-[10px] text-zinc-400 leading-relaxed mt-2.5" data-testid="attestation-quick-confirm-copy">
                  All must-haves marked Done — reminder: a $20/item fee applies to any marked Done that wasn't.
                </p>
              )}
            </>
          ) : (
            items.map((m, i) => {
              const d = decisions[m.id] || {};
              return (
                <div key={m.id} className={`tap-auto p-2.5 rounded-md border transition-colors mb-2 last:mb-0 ${d.state ? 'border-ug-border bg-ug-surface/40' : 'border-ug-gold/30 bg-ug-gold/[0.03]'}`} data-testid={`attestation-item-${i}`}>
                  <p className="text-[11px] text-zinc-100/90 leading-snug mb-2 break-words">{m.text}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button type="button" onClick={() => setState(m.id, 'done')} data-testid={`attestation-done-${i}`}
                      className={`tap-auto px-2.5 py-1 rounded-full font-sans text-[9px] font-semibold uppercase tracking-wider border transition-colors ${d.state === 'done' ? 'bg-oxidized-teal/20 border-oxidized-teal text-oxidized-teal' : 'border-ug-border text-slate-400 hover:border-oxidized-teal/50 hover:text-oxidized-teal'}`}>
                      <Check className="w-3 h-3 inline mr-1" strokeWidth={2.5} />Done
                    </button>
                    {m.id !== NOTES_ITEM_ID && (
                      <button type="button" onClick={() => setState(m.id, 'na')} data-testid={`attestation-na-${i}`}
                        title="This was not possible, or does not apply to this project. A reason is required. No fee."
                        className={`tap-auto px-2.5 py-1 rounded-full font-sans text-[9px] font-semibold uppercase tracking-wider border transition-colors ${d.state === 'na' ? 'bg-amber-500/15 border-amber-500/60 text-amber-300' : 'border-ug-border text-slate-400 hover:border-amber-500/40 hover:text-amber-300'}`}>
                        Can't do this
                      </button>
                    )}
                    {d.pre && d.state === 'done' && <span className="font-mono text-[8px] text-oxidized-teal/60">checked while working</span>}
                  </div>
                  {d.state === 'na' && (
                    <input value={d.na_reason || ''} onChange={e => setReason(m.id, e.target.value)} maxLength={200} autoFocus
                      placeholder="Why was this not possible, or why does it not apply? (required, no fee)"
                      data-testid={`attestation-na-reason-${i}`}
                      className="tap-auto mt-2 w-full bg-ug-surface border border-amber-500/30 text-zinc-100 text-[11px] rounded-md px-2.5 py-1.5 placeholder:text-white/35 focus:border-amber-500/60 outline-none" />
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="p-4 border-t border-ug-border sticky bottom-0 bg-ug-deepest flex items-center gap-3">
          {!allPre && (
            <span className="tap-auto font-mono text-[9px] text-slate-500 flex-1" data-testid="attestation-progress">
              {resolved} of {items.length} resolved
            </span>
          )}
          <Button disabled={!canSubmit || submitting} onClick={submit} data-testid="attestation-confirm-btn"
            className={`bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 font-sans text-[11px] font-semibold h-9 px-4 disabled:opacity-40 ${allPre ? 'flex-1' : ''}`}>
            {submitting ? 'Recording…' : allPre ? 'Confirm & Continue' : 'Attest & Continue to Upload'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Admin review surface: attestation record + $20 fee mechanic ──
// N/A items prominent; each Done item gets an admin-only "Flag as not done →
// $20 fee" (admin-judged, NEVER automatic); flags retractable before the
// payment settles. Tombstone variant for admin uploads with no attestation.
export function AttestationReview({ projectId, attestation, onChanged, canFlag }) {
  const [flagging, setFlagging] = useState(null); // item id with the reason input open
  const [flagReason, setFlagReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!attestation) return null;
  const tomb = attestation.source === 'admin_upload_no_attestation';
  const items = attestation.items || [];
  const naCount = items.filter(i => i.state === 'na').length;
  const applyFlag = async (itemId) => {
    if (busy || flagReason.trim().length < 5) return;
    setBusy(true);
    try {
      const r = await api.post(`/projects/${projectId}/attestation/flag-item`, { item_id: itemId, reason: flagReason.trim() });
      toast.success(r.data.fee_message || 'Item flagged');
      setFlagging(null); setFlagReason('');
      onChanged();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Failed to flag item');
    } finally { setBusy(false); }
  };
  const retract = async (itemId) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/attestation/retract-flag`, { item_id: itemId });
      toast.success('Flag retracted');
      onChanged();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Failed to retract');
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-ug-border bg-ug-surface/40 p-3" data-testid="attestation-review">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-3.5 h-3.5 text-ug-gold/70 shrink-0" />
        <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/70 flex-1">
          First-Draft Attestation
          {/* 2026-07-15 three-way split: surface which category's list was
              gated here so ops can distinguish highlight/doc/social attestations
              at a glance. Empty for legacy snapshots (pre-split) — safe fallback. */}
          {attestation.category && (
            <span
              data-testid="attestation-review-category"
              className="ml-1.5 text-ug-gold/50 normal-case tracking-normal">
              — {({ highlight: 'Highlight Videos', doc_edit: 'Doc Edits', social: 'Social Media Videos' })[attestation.category] || attestation.category}
            </span>
          )}
        </span>
        {!tomb && (
          <span className="tap-auto font-mono text-[8px] text-slate-500" data-testid="attestation-review-meta">
            {attestation.editor_name || 'Editor'} · {attestation.created_at ? new Date(attestation.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
          </span>
        )}
      </div>
      {tomb ? (
        <div className="tap-auto flex items-center gap-2 p-2.5 rounded-md bg-white/[0.03] border border-ug-border" data-testid="attestation-tombstone">
          <AlertTriangle className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-[10px] text-slate-400">Admin upload — no attestation. Items below were never confirmed by the editor.</span>
        </div>
      ) : naCount > 0 && (
        <div className="tap-auto flex items-center gap-2 p-2 rounded-md bg-amber-500/[0.08] border border-amber-500/30 mb-2" data-testid="attestation-na-banner">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
          <span className="text-[10px] text-amber-200/90 font-medium">{naCount} item{naCount !== 1 ? 's' : ''} the editor could not do. Review the reason{naCount !== 1 ? 's' : ''} before approving.</span>
        </div>
      )}
      <div className="mt-1.5">
        {items.map((it, i) => (
          <div key={it.id} className={`tap-auto p-2 rounded-md border mb-1.5 last:mb-0 ${it.disputed ? 'border-velvet-burgundy/50 bg-velvet-burgundy/[0.08]' : it.state === 'na' ? 'border-amber-500/30 bg-amber-500/[0.05]' : 'border-ug-border-subtle bg-white/[0.02]'}`} data-testid={`attestation-review-item-${i}`}>
            <div className="flex items-start gap-2">
              {it.disputed ? <Flag className="w-3 h-3 text-velvet-burgundy shrink-0 mt-[2px]" />
                : it.state === 'na' ? <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0 mt-[2px]" />

                : it.state === 'done' ? <Check className="w-3 h-3 text-oxidized-teal shrink-0 mt-[2px]" strokeWidth={2.5} />
                : <span className="w-3 h-3 rounded-[3px] border border-slate-600 shrink-0 mt-[2px]" />}
              <span className={`text-[10px] leading-snug break-words flex-1 ${it.disputed ? 'text-zinc-100/90' : 'text-zinc-100/80'}`}>{it.text}</span>
              {canFlag && !tomb && it.state === 'done' && !it.disputed && flagging !== it.id && (
                <button type="button" onClick={() => { setFlagging(it.id); setFlagReason(''); }} data-testid={`attestation-flag-btn-${i}`}
                  className="tap-auto shrink-0 font-sans text-[8px] font-semibold uppercase tracking-wider text-slate-500 hover:text-velvet-burgundy border border-ug-border hover:border-velvet-burgundy/50 rounded px-1.5 py-0.5 transition-colors">
                  Flag · $20
                </button>
              )}
            </div>
            {it.state === 'na' && it.na_reason && (
              <p className="text-[9px] text-amber-200/70 mt-1 ml-5 italic" data-testid={`attestation-review-na-reason-${i}`}>Could not do: "{it.na_reason}"</p>
            )}
            {it.disputed && it.dispute && (
              <div className="mt-1.5 ml-5" data-testid={`attestation-dispute-${i}`}>
                <p className="text-[9px] text-velvet-burgundy leading-snug">
                  Flagged not done by {it.dispute.by_name || 'admin'} — "{it.dispute.reason}"
                </p>
                <p className="text-[8px] font-mono text-slate-500 mt-0.5">{it.dispute.fee_message}</p>
                {canFlag && (
                  <button type="button" onClick={() => retract(it.id)} disabled={busy} data-testid={`attestation-retract-btn-${i}`}
                    className="tap-auto mt-1 inline-flex items-center gap-1 font-sans text-[8px] font-semibold uppercase tracking-wider text-slate-500 hover:text-zinc-200 border border-ug-border rounded px-1.5 py-0.5 transition-colors">
                    <Undo2 className="w-2.5 h-2.5" /> Retract flag
                  </button>
                )}
              </div>
            )}
            {flagging === it.id && (
              <div className="mt-1.5 ml-5 space-y-1.5">
                <input value={flagReason} onChange={e => setFlagReason(e.target.value)} maxLength={200} autoFocus
                  placeholder="Why wasn't this done? (required)" data-testid={`attestation-flag-reason-${i}`}
                  className="tap-auto w-full bg-ug-surface border border-velvet-burgundy/40 text-zinc-100 text-[10px] rounded px-2 py-1.5 placeholder:text-white/35 focus:border-velvet-burgundy/70 outline-none" />
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => applyFlag(it.id)} disabled={busy || flagReason.trim().length < 5} data-testid={`attestation-flag-apply-${i}`}
                    className="tap-auto font-sans text-[8px] font-semibold uppercase tracking-wider text-velvet-burgundy border border-velvet-burgundy/50 hover:bg-velvet-burgundy/10 rounded px-2 py-1 transition-colors disabled:opacity-40">
                    Apply $20 fee
                  </button>
                  <button type="button" onClick={() => { setFlagging(null); setFlagReason(''); }} data-testid={`attestation-flag-cancel-${i}`}
                    className="tap-auto font-sans text-[8px] font-semibold uppercase tracking-wider text-slate-500 hover:text-zinc-300 rounded px-2 py-1 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
